/**
 * 일자별 전체 사업장 예약 조회 + 직권 취소 API
 *
 * 백엔드 명세: BACKEND_API_REQUIREMENTS_DAILY_RESERVATIONS_AND_TICKETS.md
 * - GET  /api/admin/reservations/by-date
 * - POST /api/admin/reservations/{reservationSeq}/cancel
 */

import { apiClient } from './client';

// ─────────────────────────────────────────────────────────────
// 공용 도메인 타입 (HIST_RESERVATION enum 매핑)
// ─────────────────────────────────────────────────────────────

export type ReservType =
  | 'RESERV_TYPE_CLASS'
  | 'RESERV_TYPE_DAYCARE'
  | 'RESERV_TYPE_HOTEL'
  | 'RESERV_TYPE_GROOMING'
  | 'RESERV_TYPE_TRAINING';

export type ReservStatus =
  | 'RESERV_STATUS_TEMP'
  | 'RESERV_STATUS_REQUESTED'
  | 'RESERV_STATUS_UPDATED'
  | 'RESERV_STATUS_CONFIRMED'
  | 'RESERV_STATUS_CANCELED'
  | 'RESERV_STATUS_COMPLETED';

export type PaymentType = 'PAYMENT_TYPE_MUNG_COIN' | 'PAYMENT_TYPE_TICKET';

// ─────────────────────────────────────────────────────────────
// 응답 모델
// ─────────────────────────────────────────────────────────────

/** BE 응답: 일자별 예약 1건 (HIST_RESERVATION + JOIN INFO_COMPANY/USER/PET) */
export interface DailyReservationRow {
  reservationSeq: number;
  companySeq: number;
  companyName: string;
  userSeq: number;
  userName: string;
  /** 마스킹/복호화 정책에 따라 BE 가 가공 */
  userPhone: string;
  petSeq: number;
  petName: string;
  reservType: ReservType;
  status: ReservStatus;
  /** YYYY-MM-DD */
  reservStartDate: string;
  reservEndDate: string;
  /** HH:mm */
  startTime: string;
  endTime: string | null;
  paymentType: PaymentType;
  paymentCoin: number | null;
  paymentTicket: number | null;
  /** 이용권 상품명 (이용권 결제 예약인 경우) */
  ticketName: string | null;
  memo: string | null;
  /** YYYY-MM-DD HH:mm:ss */
  createdAt: string;
}

/** BE 응답: 예약 취소 결과 (이용권/코인 cascade 환원 카운트 포함) */
export interface CancelReservationResult extends DailyReservationRow {
  canceledAt: string;
  /** 함께 환원된 HIST_TICKET_USE row 개수 */
  cancelledTicketUses: number;
  /** 함께 환원된 이용권 총 횟수 (호텔은 박수 전량 = QTY 합) */
  restoredTicketQty: number;
  /** 함께 환원된 코인 총액 (코인 결제 예약일 때) */
  restoredCoinAmount: number;
}

// ─────────────────────────────────────────────────────────────
// 요청 파라미터
// ─────────────────────────────────────────────────────────────

export interface GetDailyReservationsParams {
  /** YYYY-MM-DD (필수) */
  date: string;
  /** 사업장명/보호자명/반려동물명 부분 일치 */
  keyword?: string;
  reservType?: ReservType | 'ALL';
  status?: ReservStatus | 'ALL';
}

/** BE 응답: 예약 승인 결과 (AdminReservationResponseDto) */
export interface ApproveReservationResult {
  id: number;
  userSeq: number;
  petSeq: number;
  companySeq: number;
  productSeq: number | null;
  reservType: ReservType;
  status: ReservStatus;
  reservDate: string;
  reservEndDate: string | null;
  reservTime: string | null;
  endTime: string | null;
  memo: string | null;
  createdAt: string;
}

export interface CancelReservationParams {
  reservationSeq: number;
  /** 어드민 메모/사유 (선택) */
  reason?: string;
}

// ─────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────

/**
 * BE 응답 `{ data: T }` 형태(또는 ServiceResult) 언랩.
 * notifications.ts 등 다른 서비스와 동일한 패턴.
 */
function unwrap<T>(resp: { data?: unknown }): T {
  const raw = resp.data as { data?: T } | T | undefined;
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>)
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export const dailyReservationsApi = {
  /**
   * 예약 승인 (운영자 대리).
   * - 예약신청(REQUESTED)/예약수정(UPDATED) 상태만 가능.
   * - BE 가 사업자 앱 승인과 동일하게 보호자 알림장·푸시·알림톡을 발송한다.
   */
  approveReservation: async (
    reservationSeq: number,
  ): Promise<ApproveReservationResult> => {
    const response = await apiClient.post(
      `/admin/reservations/${reservationSeq}/approve`,
    );
    return unwrap<ApproveReservationResult>(response);
  },

  /** 일자별 전체 사업장 예약 조회 */
  getByDate: async (
    params: GetDailyReservationsParams,
  ): Promise<DailyReservationRow[]> => {
    const response = await apiClient.get('/admin/reservations/by-date', {
      params: {
        date: params.date,
        keyword: params.keyword,
        reservType: params.reservType ?? 'ALL',
        status: params.status ?? 'ALL',
      },
    });
    return unwrap<DailyReservationRow[]>(response);
  },

  /**
   * 예약 직권 취소.
   * - 이용권 결제 예약은 BE 가 같은 트랜잭션으로 HIST_TICKET_USE 환원 수행.
   * - 응답의 cancelledTicketUses/restoredTicketQty 로 환원 결과 확인 가능.
   */
  cancelReservation: async (
    params: CancelReservationParams,
  ): Promise<CancelReservationResult> => {
    const response = await apiClient.post(
      `/admin/reservations/${params.reservationSeq}/cancel`,
      { reason: params.reason },
    );
    return unwrap<CancelReservationResult>(response);
  },
};
